import { mapActions, mapGetters, mapMutations } from 'vuex'
import { camelCase, debounce, snakeCase } from 'lodash'
import moment from 'moment'

import mixins from '@/mixins'
import course from '@/mixins/course'
import validation from '@/mixins/validation'

import Draggable from 'vuedraggable'
import BulkAccessModal from '@/components/course-access/BulkAccessModal.vue'

import { coursePackagesTableHead } from '@/modules/courses/helpers/packages'

/** Match backend config/course.php */
const PACKAGE_ACCESS_DEFAULT_DAYS_FROM_START = 430
const PACKAGE_ACCESS_MIN_DAYS_AFTER_COURSE_START = 30

export default {
	name		: 'courses-packages',
	mixin		: [mixins, course, validation],
	data () {
		return {
			accessDateMenu  : false,
			payload         : {
				name            : '',
				crmId           : '',
				isDip           : false,
				sort            : '',
				zohoDealId      : '',
				zohoProductName : '',
				accessExpiresAt : ''
			},
			packages        : [],
			valid           : true,
			showAction      : false,
			showPopupDelete : false,
			removingItem    : null,
			typeDialog      : 'create',
			bulkAccessDialog: false,
			selectedPackage : {}
		}
	},
	components	: {
		Draggable,
		BulkAccessModal
	},
	watch		: {
		'list' () {
			this.packages = this.list
		},
		'$route.params.course_id': {
			immediate: false,
			handler () {
				this.loadPackages()
			}
		}
	},
	computed	: {
		...mapGetters({
			list            : 'coursesPackages/list',
			profile         : 'profile/profile',
			setting         : 'courses/setting',
			listLoading     : 'coursesPackages/listLoading',
			requestLoading  : 'coursesPackages/requestLoading',
			courseInfo      : 'coursesInfo/info'
		}),
		coursePackagesTableHead,
		courseId () {
			return parseInt(this.$route.params.course_id)
		},
		courseStartedAt () {
			const raw = this.courseInfo && this.courseInfo.startedAt

			if (!raw) {
				return null
			}

			return moment(raw).format('YYYY-MM-DD')
		},
		todayIso () {
			return moment().format('YYYY-MM-DD')
		},
		accessDatePickerMin () {
			let min = moment().startOf('day')

			if (this.courseStartedAt) {
				const fromCourseStart = moment(this.courseStartedAt)
					.add(PACKAGE_ACCESS_MIN_DAYS_AFTER_COURSE_START, 'days')

				if (fromCourseStart.isAfter(min)) {
					min = fromCourseStart
				}
			}

			return min.format('YYYY-MM-DD')
		},
		suggestedAccessExpiresAt () {
			const base = this.courseStartedAt
				? moment(this.courseStartedAt).startOf('day')
				: moment().startOf('day')

			return base.clone()
				.add(PACKAGE_ACCESS_DEFAULT_DAYS_FROM_START, 'days')
				.format('YYYY-MM-DD')
		},
		accessExpiresDateRules () {
			const minDate = this.accessDatePickerMin

			return [
				v => !v || v >= this.todayIso || 'Date cannot be in the past',
				v => !v || v >= minDate || `Earliest allowed date: ${minDate} (today or course start + ${PACKAGE_ACCESS_MIN_DAYS_AFTER_COURSE_START} days)`
			]
		},
		showAccessDateEmptyWarning () {
			return !this.payload.accessExpiresAt
		},
		accessDateEmptyWarningText () {
			if (this.typeDialog === 'create') {
				return `If left empty, the server will set ${this.suggestedAccessExpiresAt} (course start + ${PACKAGE_ACCESS_DEFAULT_DAYS_FROM_START} days).`
			}

			return 'If left empty, package access will not expire (unlimited access).'
		},
		showAccessDateEarlyWarning () {
			const selected = this.payload.accessExpiresAt

			if (!selected || !this.courseStartedAt) {
				return false
			}

			const selectedMoment = moment(selected, 'YYYY-MM-DD', true)
			const suggestedMoment = moment(this.suggestedAccessExpiresAt, 'YYYY-MM-DD', true)

			if (!selectedMoment.isValid() || !suggestedMoment.isValid()) {
				return false
			}

			return selectedMoment.isBefore(suggestedMoment)
				&& selectedMoment.isSameOrAfter(this.accessDatePickerMin)
		},
		accessDateHint () {
			if (this.courseStartedAt) {
				return `Optional. Recommended: ${this.suggestedAccessExpiresAt} (course start + ${PACKAGE_ACCESS_DEFAULT_DAYS_FROM_START} days). Earliest if set: ${this.accessDatePickerMin}.`
			}

			return `Optional. Recommended: ${this.suggestedAccessExpiresAt}. Earliest if set: ${this.accessDatePickerMin}.`
		}
	},
	created () {
		this.setCourseStep(2);
		
		console.log('course_id:', this.$route.params.course_id)
		
		this.loadCourseInfo()
		this.loadPackages()
	},
	methods		: {
		...mapActions({
			fetchList       : 'coursesPackages/PACKAGES_GET_LIST',
			create          : 'coursesPackages/PACKAGES_CREATE',
			update          : 'coursesPackages/PACKAGES_UPDATE',
			updateSort      : 'coursesPackages/PACKAGES_UPDATE_SORT',
			remove          : 'coursesPackages/PACKAGES_DELETE',
			fetchCourseInfo : 'coursesInfo/INFO_GET_COURSE'
		}),
		...mapMutations({
			changeSkip	: 'coursesPackages/PACKAGES_CHANGE_SKIP'
		}),
		async loadCourseInfo () {
			if (!this.$route.params.course_id) {
				return
			}

			try {
				await this.fetchCourseInfo({ id: this.$route.params.course_id })
			} catch (e) {
				console.log(e)
			}
		},
		async loadPackages () {
			console.log('loadPackages')
			
			await this.fetchList({
				id: this.$route.params.course_id
			})
			
			this.packages = this.list
		},
		changePopupAction (item, status = false) {
			if (!item && status) {
				this.resetContent();
				this.showAction = true;
			}
			
			if (!item && !status) {
				this.showAction = false;
				this.resetContent();
			}
			
			if (item && status) {
				this.showAction = true;
				this.setContent(item);
			}
		},
		changePopupDelete (item) {
			this.removingItem = item;
			this.removingItem.course_id = this.$route.params.course_id;
			this.showPopupDelete = true;
		},
		deletePackage () {
			this.remove(this.removingItem).then(() => {
				this.$toasted.success(this.$t('success_deleted'));
				this.showPopupDelete = false;
			})
		},
		resetContent () {
			this.payload = {
				name            : '',
				crmId           : '',
				isDip           : false,
				sort            : '',
				zohoDealId      : '',
				zohoProductName : '',
				accessExpiresAt : ''
			}
			
			this.typeDialog = 'create'
			
			setTimeout(() => {
				if (this.$refs.form) {
					this.$refs.form.reset()
				}
			}, 10)
		},
		setContent (item) {
			const obj = {};
			
			console.log('setContent:');
			
			Object.entries(item).forEach(e => {
				//obj[camelCase(e[0])] = e[1];
				
				if(e[0] == 'is_dip'){
					obj[camelCase(e[0])] = e[1] == 1;
				}else{
					obj[camelCase(e[0])] = e[1];
				}
				
				console.log(obj[camelCase(e[0])]);
			});
			
			this.typeDialog = 'update'
			
			if (obj.accessExpiresAt === null || obj.accessExpiresAt === undefined) {
				obj.accessExpiresAt = ''
			}
			
			Object.assign(this.payload, obj)
		},
		createRequestPayload () {
			const data = {}
			const allowedKeys = ['name', 'crmId', 'isDip', 'sort', 'zohoDealId', 'zohoProductName', 'accessExpiresAt', 'id']
			
			Object.entries(this.payload)
				.filter(([key]) => allowedKeys.includes(key))
				.forEach(([key, val]) => {
					if (key === 'id' && !val) {
						return
					}

					if (key === 'accessExpiresAt' && !val) {
						data.access_expires_at = null
						return
					}
					
					data[snakeCase(key)] = val
				})
			
			data.course_id = this.$route.params.course_id
			
			return data
		},
		changeSortPackages: debounce(function (e) {
			if (e.hasOwnProperty('moved')) {
				const sortsList = this.packages.map(e => e.id);
				
				const formData = {
					course_id   : this.$route.params.course_id,
					packages    : sortsList
				}
				
				this.updateSort(formData)
				.then(() => {
					this.$toasted.success(this.$t('success_change_position'))
				})
			}
		}, 2000),
		submit () {
			this.$refs.form.validate()
			
			if (this.valid) {
				if (this.typeDialog === 'update') {
					this.update(this.createRequestPayload()).then(() => {
						this.$toasted.success(this.$t('success_updated'))
						this.showAction = false
						this.resetContent()
					}).catch(() => {})
				} else {
					this.create(this.createRequestPayload()).then(() => {
						this.$toasted.success(this.$t('success_created'))
						this.showAction = false
						this.resetContent()
					}).catch(() => {})
				}
			}
		},
		back () {
			this.goToStep('courses.info', 1)
		},
		openBulkAccess (packageItem) {
			this.selectedPackage = packageItem
			this.bulkAccessDialog = true
			
			console.log('Opening bulk access for package:', packageItem)
		},
		onAccessUpdated () {
			console.log('Access updated, refreshing package list...')
			this.$toasted.success(this.$t('accessUpdatedSuccess') || 'Доступ успішно оновлено')
			
			// Optionally refresh the package list
			this.fetchList({
				id: this.$route.params.course_id
			})
		},
		formatAccessExpires (value) {
			if (value === null || value === undefined || value === '') {
				return '—'
			}
			if (typeof value === 'string') {
				return value.length >= 10 ? value.slice(0, 10) : value
			}
			return String(value)
		}
	}
}
